import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody';
import Paragraph from '../../Paragraph/Paragraph';
import ModalButtons from '../../Modal/ModalButtons';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { authScopeStringGetHelper } from '../../../util/helpers/authScopeHelpers';
import { JWT_TOKEN } from '../../../constants/localStorage';
import {
  getCurrentAccountCode,
  getCurrentAccountUid,
  selectNagDashboard,
} from '../../../store/selectors/accountSelectors';
import { UPGRADE_STATUS_TYPES } from '../../../constants/nagDashboardContants';

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${pxToRem(16)};
`;

const getPrimaryLabel = (isUpgradeProgress, t) =>
  isUpgradeProgress ? t('common.continue') : t('common.getStarted');

const BodyTitle = styled.h3`
  font-size: ${pxToRem(18)};
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin-bottom: ${pxToRem(8)};
`;

const BasicAccountRestristionsModal = ({
  isOpen,
  close,
  title,
  description,
  isHiddenButton,
  showDoItLater,
  icon,
}) => {
  const { t } = useTranslation();
  const { RegistrationUrlV2: registrationApi } = useSelector(selectSettings);
  const token = authScopeStringGetHelper(JWT_TOKEN);
  const accountUid = useSelector(getCurrentAccountUid);
  const accountCode = useSelector(getCurrentAccountCode);
  const nagDashboard = useSelector(selectNagDashboard);
  const isUpgradeProgress =
    nagDashboard?.UpgradeStatusType === UPGRADE_STATUS_TYPES.InProgress;

  const goToRegistrationPage = () => {
    window.location.replace(
      `${registrationApi}/choose-application?accountCode=${accountCode}&accountUid=${accountUid}&token=${token}`,
    );
  };

  return (
    <Modal
      title={icon ? undefined : title}
      isOpen={isOpen}
      size={showDoItLater ? 'lg' : 'sm'}
      close={close}
      closeIconVisible={!icon}
      hideHeader={!!icon}
      verticalCenter={!!icon}
      isCenterModal={showDoItLater}
    >
      <ModalBody style={{ padding: showDoItLater ? '20px 40px' : 'unset' }}>
        {icon && (
          <>
            <IconWrapper>{icon}</IconWrapper>
            <BodyTitle>{title}</BodyTitle>
          </>
        )}
        <Paragraph centerText={!!icon}>{description}</Paragraph>
        {!isHiddenButton && (
          <ModalButtons
            isVertical={!showDoItLater}
            isHorizontal={showDoItLater}
            buttonStylesReversed={showDoItLater}
            textTransform={showDoItLater ? 'none' : 'uppercase'}
            marginTop
            showOnlyPrimary={!showDoItLater}
            primaryButtonProps={{
              label: showDoItLater
                ? t('common.doItLater')
                : getPrimaryLabel(isUpgradeProgress, t),
              onClick: showDoItLater ? close : goToRegistrationPage,
            }}
            secondaryButtonProps={{
              label: isUpgradeProgress
                ? t('common.continue')
                : t('common.verifyAccount'),
              onClick: goToRegistrationPage,
            }}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

BasicAccountRestristionsModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  isHiddenButton: PropTypes.bool,
  showDoItLater: PropTypes.bool,
  icon: PropTypes.node,
};

export default BasicAccountRestristionsModal;
